import { useEffect, useRef } from 'react';

interface UseScrollTriggerOptions {
  /**
   * 触发动画的CSS类名
   */
  triggerClass: string;
  /**
   * 触发线位置，相对于视口高度的比例 (0-1)
   * @default 0.5
   */
  triggerRatio?: number;
  /**
   * 自定义滚动容器选择器
   * @default '.aixue-ai-scroll'
   */
  scrollContainerSelector?: string;
  /**
   * 是否在视口变化时重新检测
   * @default true
   */
  enableResizeDetection?: boolean;
  /**
   * 滚动触发回调
   */
  scrollTriggerCallback?: () => void;
}

/**
 * 滚动触发动画的hooks
 * 当元素滚动到指定位置时，添加指定的CSS类名触发动画
 */
export function useScrollTrigger(options: UseScrollTriggerOptions) {
  const {
    triggerClass,
    triggerRatio = 0.5,
    scrollContainerSelector = '.aixue-ai-scroll',
    enableResizeDetection = true,
    scrollTriggerCallback = () => {}
  } = options;

  const rootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;
    
    let hasTriggered = false;
    let currentTarget: Window | HTMLElement | null = null;

    const onScroll = () => {
      const rect = rootEl.getBoundingClientRect();
      const triggerLine = window.innerHeight * triggerRatio;
      
      if (!hasTriggered && rect.top <= triggerLine) {
        hasTriggered = true;
        if (triggerClass) {
          rootEl.classList.add(triggerClass);
        }
        scrollTriggerCallback();
      }
    };

    const setupScrollListener = () => {
      // 移除旧的监听器
      if (currentTarget) {
        currentTarget.removeEventListener('scroll', onScroll as any);
        currentTarget = null;
      }
      
      // 重新检测滚动容器
      const scroller = document.querySelector(scrollContainerSelector) as HTMLElement | null;
      const useWindow = !scroller || scroller.scrollHeight <= scroller.clientHeight;
      
      // 确保滚动容器有效
      if (useWindow) {
        currentTarget = window;
      } else if (scroller && scroller.scrollHeight > scroller.clientHeight) {
        currentTarget = scroller;
      } else {
        // 如果自定义滚动容器无效，回退到window
        currentTarget = window;
      }
      
      // 添加新的监听器
      if (currentTarget) {
        currentTarget.addEventListener('scroll', onScroll as any, { passive: true } as any);
      }
    };

    const onResize = () => {
      // 视口大小变化时重新检测
      if (!hasTriggered) {
        onScroll();
      }
      // 重新设置滚动监听器
      setupScrollListener();
    };

    // 初始设置
    setupScrollListener();
    onScroll();
    
    // 添加窗口大小变化监听
    if (enableResizeDetection) {
      window.addEventListener('resize', onResize, { passive: true });
    }
    
    return () => {
      if (currentTarget) {
        currentTarget.removeEventListener('scroll', onScroll as any);
      }
      if (enableResizeDetection) {
        window.removeEventListener('resize', onResize);
      }
    };
  }, [triggerClass, triggerRatio, scrollContainerSelector, enableResizeDetection]);

  return rootRef;
}
